import React, { useState } from 'react';
import './App.css';
import styled from "styled-components";
import banner from "./assets/images/pixel_banner_plain_cropped.png";
import wing from "./assets/images/banner_wing.png";
import marc from "./assets/images/marc_transparent.png";
import heart from "./assets/images/heart.png";
import { useNavigate } from "react-router-dom";
import { Headline, Paragraph } from "@/components/typography";

const AppContainer = styled.div`
  background: black;
  color: white;
  width: 100%;
  min-width: 435px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

/**
 * TopSection / BottomSection control where the menu sits vertically.
 * At XL the 1:1 flex split keeps the menu in the bottom half.
 * At smaller breakpoints, TopSection shrinks so the menu rises to stay
 * aligned with Marc's vertical position.
 */
const TopSection = styled.div`
  width: 100%;
  display: flex;
  flex: 1;

  @media (max-width: 992px) {
    flex: 0.7;
  }
  @media (max-width: 768px) {
    flex: 0.5;
  }
  @media (max-width: 600px) {
    flex: 0.3;
  }
  @media (max-height: 700px) {
    flex: 0.3;
  }
  @media (max-height: 500px) {
    flex: 0.15;
  }
`;

/**
 * BottomSection centers the menu with a 5% left bias (slightly right of center).
 * At SM: pure center (no Marc visible).
 * At SHORT+wide: menu pushed to the right 60%.
 */
const BottomSection = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  justify-content: center;

  @media (max-width: 600px) {
    padding-left: 0;
  }

  @media (max-height: 500px) and (min-width: 769px) {
    justify-content: flex-end;
    padding-left: 0;
    padding-right: 5%;
  }
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding-left: 10vw;
  z-index: 4;
  position: relative;

  @media (max-width: 992px) {
    padding-top: 4vh;
  }
  @media (max-width: 600px) {
    padding-top: 8vh;
  }
  @media (max-height: 700px) {
    padding-top: 14vh;
  }
  @media (max-height: 500px) {
    padding-top: 14vh;
  }

  /* Wide + short: 2-column grid, constrained to 60% width */
  @media (max-height: 500px) and (min-width: 769px) {
    display: grid;
    grid-template-columns: .5fr 2fr;
    gap: 4px 4px;
    padding-top: 24vh;
    width: 50%;
  }
`;

const SelectItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  cursor: pointer;

  @media (max-width: 992px) {
     padding: 4px 0;
  }

  @media (max-height: 500px) and (min-width: 769px) {
    padding: 4px 0;
  }
`;

const Banner = styled.div`
  background-image: url(${banner});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  width: 100%;
  height: 260px;
  z-index: 1;
  position: absolute;
  top: 86px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 992px) {
    top: 60px;
    height: 260px;
  }
  @media (max-width: 768px) {
    top: 24px;
    height: 200px;
  }
  @media (max-width: 600px) {
    top: 12px;
    height: 120px;
  }
  @media (max-height: 700px) {
    top: 20px;
    height: 200px;
  }
  @media (max-height: 500px) {
    top: 8px;
    height: 100px;
  }
`;

/**
 * Wing extends the banner edge-to-edge via repeat-x.
 * Positioned vertically to sit behind the banner's center at each breakpoint.
 * Wing top = (banner top + banner height / 2) - wing height / 2
 */
const Wing = styled.div`
  background-image: url(${wing});
  background-repeat: repeat-x;
  background-size: contain;
  width: 100%;
  height: 130px;
  z-index: 0;
  position: absolute;
  top: 151px;
  left: 0;

  @media (max-width: 992px) {
    top: 125px;
    height: 130px;
  }
  @media (max-width: 768px) {
    top: 74px;
    height: 100px;
  }
  @media (max-width: 600px) {
    top: 42px;
    height: 60px;
  }
  @media (max-height: 700px) {
    top: 70px;
    height: 100px;
  }
  @media (max-height: 500px) {
    top: 33px;
    height: 50px;
  }
`;

const Marc = styled.div`
  background-image: url(${marc});
  background-repeat: no-repeat;
  background-size: contain;
  height: 600px;
  width: 362px;
  z-index: 2;
  position: absolute;
  top: 220px;
  left: 0;

  /* Width breakpoints */
  @media (max-width: 992px) {
    height: 450px;
    width: 272px;
    top: 180px;
  }
  @media (max-width: 768px) {
    height: 350px;
    width: 212px;
    top: 140px;
  }
  @media (max-width: 600px) {
    display: none;
  }

  /* Wide + short: keep Marc bold, just shift up (clipped at bottom = intentional) */
  @media (max-height: 700px) and (min-width: 769px) {
    top: 100px;
  }
  @media (max-height: 500px) and (min-width: 769px) {
    top: 40px;
  }

  /* Narrow + short: shrink Marc */
  @media (max-height: 700px) and (max-width: 768px) {
    height: 320px;
    width: 193px;
    top: 120px;
  }
  @media (max-height: 500px) and (max-width: 768px) {
    height: 220px;
    width: 133px;
    top: 80px;
  }
`;

type HeartProps = {
  visible: boolean;
}
const Heart = styled.div<HeartProps>`
  visibility: ${props => props.visible ? "visible" : "hidden"};
  background-image: url(${heart});
  background-repeat: no-repeat;
  background-size: contain;
  height: 30px;
  width: 30px;
  padding-right: 48px;
  z-index: 4;

  @media (max-height: 500px) {
    padding-right: 16px;
  }
`;

const Footer = styled.div`
  display: flex;
  padding-bottom: 48px;
  z-index: 4;
  position: relative;
`;

function App() {
  const navigate = useNavigate();
  const [hovering, setHovering] = useState("Resume");
  const handleHover = (selection: string) => {
    setHovering(selection)
  }
  const handleClick = (selection: string) => {
    navigate(selection)
  }
  return (
    <AppContainer>
      <TopSection>
        <Banner>
          <Headline style={{ zIndex: 3 }}>
            Marc O Cleirigh
          </Headline>
        </Banner>
        <Wing />
      </TopSection>
      <Marc />
      <BottomSection>
        <SelectContainer>
          <SelectItemContainer
            onClick={() => handleClick("Resume")}
            onMouseOver={() => handleHover("Resume")}>
            <Heart visible={hovering === "Resume"} />
            <Paragraph>
              Resume
            </Paragraph>
          </SelectItemContainer>
          <SelectItemContainer
            onClick={() => handleClick("Interests")}
            onMouseOver={() => handleHover("Interests")}>
            <Heart visible={hovering === "Interests"} />
            <Paragraph>
              Interests
            </Paragraph>
          </SelectItemContainer>
          <SelectItemContainer
            onClick={() => window.open("https://github.com/moclei", "_blank")}
            onMouseOver={() => handleHover("GitHub")}>
            <Heart visible={hovering === "GitHub"} />
            <Paragraph>
              GitHub
            </Paragraph>
          </SelectItemContainer>
          <SelectItemContainer
            onClick={() => window.open("https://www.linkedin.com/in/marc-o-cleirigh-4683a5214/", "_blank")}
            onMouseOver={() => handleHover("LinkedIn")}>
            <Heart visible={hovering === "LinkedIn"} />
            <Paragraph>
              LinkedIn
            </Paragraph>
          </SelectItemContainer>
        </SelectContainer>
      </BottomSection>
      <Footer>
        <Paragraph>TM and © 1988 MOC Industry Co. Ltd.</Paragraph>
      </Footer>
    </AppContainer>
  );
}

export default App;
